import { Component, OnInit, signal, computed } from '@angular/core';
import { SelectItem } from 'primeng/api';
import { BaseComponent } from '../../../../core/abstracts/baseComponent';
import { Actions, ofType } from '@ngrx/effects';
import { State } from '../../../../core/store';
import { Store } from '@ngrx/store';
import { DateRangeModel } from '../../../../models/dateRangeModel';
import { PagingResultsModel } from '../../../../models/pagingResultsModel';
import { DropdownFilterService } from '../../../../core/services/dropdown-filter.service';
import { exportEmployeeTerminationListToExcel, exportEmployeeTerminationListToExcelSuccess, getEmployeeTerminationList, loadEmployeeUsers, loadViewableCampuses } from '../../../../core/store/employee/employee.action';
import { TerminatedEmployeeModel } from '../../../../models/userProfileModel';
import { deepClone, downloadFile } from '../../../../helpers/utils';
import { tap } from 'rxjs';
import { Router } from '@angular/router';
import { UserAutoCompleteItem, ModifiedSelectItem } from '../../../../models/utilModels';

@Component({
    selector: 'app-report-terminated',
    templateUrl: './report-terminated.component.html',
    styleUrls: ['./report-terminated.component.scss'],
    standalone: false
})
export class ReportTerminatedComponent extends BaseComponent implements OnInit {
  list: PagingResultsModel<TerminatedEmployeeModel>;
  filter: any;
  canEditUser: boolean = false;
  canViewEmployeeExclusionReports: boolean = false;
  
  loadingTerminationList: boolean = true;
  loadingTerminationExcel: boolean = false;

  campusList: ModifiedSelectItem[];
  campusId: number = 0;

  selectedUserEmployee: UserAutoCompleteItem;
  userEmployeeList: UserAutoCompleteItem[];
  userId: number = 0;

  dateRangeList: DateRangeModel[];
  dateRange: DateRangeModel;
  startDate: string = null;
  endDate: string = null;
  isCustomDateRange: boolean = false; 

  totalRecords = signal<number>(0);
  dropdownOptions = computed(() => {
    var total = this.totalRecords();
    return [
      { label: '20', value: 20 },
      { label: '50', value: 50 },
      { label: '100', value: 100 },
      { label: '200', value: 200 },
      { label: '500', value: 500 },
      { label: 'All', value: total > 0 ? total : 20 } 
    ];
  });
  
  constructor(
    private router: Router,
    private actions$: Actions,
    private store: Store<State>,
    private dropdownFilterService: DropdownFilterService) {
    super();
  }

  ngOnInit() {
    this.subs.push(
      this.store.select(s => s.accountState.user?.permissionIDs).subscribe(permissionIDs => {
        this.canEditUser = permissionIDs && !!permissionIDs.find(p => p === 6001 || p === 6002); //HR Manager, HR Super Admin
        this.canViewEmployeeExclusionReports = permissionIDs && !!permissionIDs.find(p => p === 3310); //View employee exclusion reports
      }),
      this.store.select(s => s.employeeState.loadingTerminationList).subscribe(loadingTerminationList => {
        this.loadingTerminationList = loadingTerminationList;
      }),
      this.store.select(s => s.employeeState.loadingTerminationExcel).subscribe(loadingTerminationExcel => {
        this.loadingTerminationExcel = loadingTerminationExcel;
      }),
      this.store.select(s => s.employeeState.campuses).subscribe(campuses => {
        this.campusList = campuses && campuses.length > 0 ? campuses
          : [{ label: 'loading...', value: undefined }];
      }),
      this.store.select(s => s.employeeState.employeeUsers).subscribe(users => {
        this.userEmployeeList = users.map(x => ({
          id: x.value,
          name: x.label
        }));
      }),
      this.store.select(s => s.employeeState.terminationList).subscribe(terminationList => {
        this.list = terminationList;

  	    if(terminationList != null)
     	    this.totalRecords.set(terminationList.totalRecords);
      }),
      this.actions$.pipe(
        ofType(exportEmployeeTerminationListToExcelSuccess),
        tap(action => {
          downloadFile(action.doc, 'EmployeeSeparation.xlsx');
        })
      ).subscribe()
    );

    this.dateRangeList = this.dropdownFilterService.getLast120DayDateRanges();
    if (this.dateRangeList && this.dateRangeList.length > 0) {
      this.dateRange = this.dateRangeList[0];
    }
    this.store.dispatch(loadViewableCampuses());
  }

  changedDateRange() {
    if (this.dateRange.isCustom) {
      this.isCustomDateRange = true;
      return; //reload after manually changing date
    }

    this.isCustomDateRange = false;
    this.startDate = this.dateRange.startDate;
    this.endDate = this.dateRange.endDate;
    this.reloadList(this.filter);
  }


  reloadList(event) {
    this.filter = event;
    this.filter = deepClone(this.filter);
    this.filter.filters.userId = { Name: 'userId', Value: { value: this.userId.toString() } };
    this.filter.filters.programId = { Name: 'programId', Value: { value: this.campusId.toString() } };
    this.filter.filters.startDate = { Name: 'startDate', Value: { value: this.startDate } };
    this.filter.filters.endDate = { Name: 'endDate', Value: { value: this.endDate } };

    this.store.dispatch(getEmployeeTerminationList({
      filter: this.filter
    }));
  }

  getStaffList(event) {    
    this.store.dispatch(loadEmployeeUsers({
      prefixText: event.query,
      programId: this.campusId,
      payrollClassificationId: null,
      showTerminated: true,
      showInactive: true
    }));
  }

  reloadListAfterStaffChage(selectedUser) {
    if (typeof selectedUser.value.id !== 'number') {
      return;
    }

    this.userId = selectedUser.value.id;
    this.reloadList(this.filter);
  }

  reloadListAfterClear(event) {
    
    this.userId = 0;
    this.reloadList(this.filter);
  }

  exportToExcel() {
    this.filter = deepClone(this.filter);
    this.filter.filters.userId = { Name: 'userId', Value: { value: this.userId.toString() } };
    this.filter.filters.programId = { Name: 'programId', Value: { value: this.campusId.toString() } };
    this.filter.filters.startDate = { Name: 'startDate', Value: { value: this.startDate } };
    this.filter.filters.endDate = { Name: 'endDate', Value: { value: this.endDate } };

    this.store.dispatch(exportEmployeeTerminationListToExcel({
      filter: this.filter
    }));
  }

  editEmployee(employee: TerminatedEmployeeModel = null) {
    const url = this.router.serializeUrl(
      this.router.createUrlTree(['/employee-file/detail/' + employee.userId])
    );

    window.open(url, '_blank');

    //this.router.navigate(['/employee-file/detail/' + employee.userId]);
  }
}
